import React, { useState, useEffect, Fragment } from 'react';
import {
  Box, Typography, CircularProgress, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, ListItemIcon, ListItemButton, Divider, Chip
} from '@mui/material';
import {
  Folder, Refresh, Close, FolderOpen
} from '@mui/icons-material';
import apiService from '../services/api.service';

const FolderSelector = ({ 
  scenePath, 
  rendererId, 
  onFolderSelect, 
  title = "Change Loadpart",
  placeholder = "Choose a folder from the scene assets",
  selectedPath = null,
  organizationName: propOrganizationName = null,
  sceneName: propSceneName = null
}) => {
  const [baseAssetPath, setBaseAssetPath] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [sceneName, setSceneName] = useState('');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderDialog, setFolderDialog] = useState({
    open: false,
    folders: [],
    selectedFolder: null,
    currentPath: '',
    breadcrumbs: []
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    folder: null,
    message: ''
  });

  useEffect(() => {
    if (rendererId) {
      loadFolders();
    }
  }, [rendererId]);

  // Initialize selectedFolder from selectedPath prop
  useEffect(() => {
    if (selectedPath && !selectedFolder) {
      // Create a folder object from the selectedPath
      const folderName = selectedPath.split('/').pop() || selectedPath;
      setSelectedFolder({
        name: folderName,
        path: selectedPath,
        type: 'folder'
      });
    }
  }, [selectedPath, selectedFolder]);

  const fetchRendererDetails = async (rendererId) => {
    try {
      const response = await apiService.getRendererDetails(rendererId);
      
      if (response && response.organization && response.scene_path) {
        return response;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const loadFolders = async () => {
    if (propOrganizationName && propSceneName) {
      setOrganizationName(propOrganizationName);
      setSceneName(propSceneName);
      
      const assetsPath = `scenes/${propOrganizationName}/${propSceneName}/assets/`;
      await loadFoldersFromPath(assetsPath, propOrganizationName, propSceneName);
      return;
    }

    if (!rendererId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const rendererDetails = await fetchRendererDetails(rendererId);
      
      if (rendererDetails) {
        const { organization, scene_path } = rendererDetails;
        const scenePathParts = scene_path.split('/').filter(Boolean);
        const extractedOrg = scenePathParts[1]; 
        const extractedScene = scenePathParts[2];         
        setOrganizationName(extractedOrg);
        setSceneName(extractedScene);
        
        const assetsPath = `scenes/${extractedOrg}/${extractedScene}/assets/`;
        await loadFoldersFromPath(assetsPath, extractedOrg, extractedScene);
      } else {
        setError('Failed to fetch renderer details');
      }
      
    } catch (err) {
      setError(err.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const loadFoldersFromPath = async (path, organizationName, sceneName) => {
    try {
      if (!organizationName || !sceneName) {
        throw new Error('Organization and scene names are required');
      }
      
      const response = await apiService.listS3Objects({
        prefix: path,
        organizationName,
        sceneName,
        delimiter: '/'
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to list folders');
      }
      
      const transformedFolders = [];
      
      if (response.data.folders && response.data.folders.length > 0) {
        response.data.folders.forEach(folder => {
          transformedFolders.push({
            name: folder.name,
            type: 'folder',
            path: folder.path,
            size: 'Folder',
            lastModified: folder.lastModified
          });
        });
      }
      
      setFolders(transformedFolders);
      
      setFolderDialog(prev => ({
        ...prev,
        folders: transformedFolders,
        currentPath: path,
        breadcrumbs: [{ name: 'Assets', path: path }]
      }));
      
    } catch (err) {
      setError('Failed to load folders: ' + err.message);
    }
  };

  const loadSubFolders = async (folderPath, parentPath = '') => {
    try {
      const response = await apiService.listS3Objects({
        prefix: folderPath,
        organizationName,
        sceneName,
        delimiter: '/'
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to list subfolders');
      }
      
      const subFolders = [];
      
      if (response.data.folders && response.data.folders.length > 0) {
        response.data.folders.forEach(folder => {
          subFolders.push({
            name: folder.name,
            type: 'folder',
            path: folder.path,
            size: 'Folder',
            lastModified: folder.lastModified
          });
        });
      }
      
      const pathParts = folderPath.split('/').filter(Boolean);
      const breadcrumbs = [];
      
      const assetsIndex = pathParts.indexOf('assets');
      if (assetsIndex >= 0) {
        breadcrumbs.push({ name: 'Assets', path: pathParts.slice(0, assetsIndex + 1).join('/') });
        
        for (let i = assetsIndex + 1; i < pathParts.length; i++) {
          const segmentPath = pathParts.slice(0, i + 1).join('/');
          breadcrumbs.push({
            name: pathParts[i],
            path: segmentPath
          });
        }
      }
      
      setFolderDialog(prev => ({
        ...prev,
        folders: subFolders,
        selectedFolder: null,
        currentPath: folderPath,
        breadcrumbs: breadcrumbs
      }));
      
    } catch (err) {
      setError('Failed to load subfolders: ' + err.message);
    }
  };

  const handleDialogFolderDoubleClick = (folder) => {
    loadSubFolders(folder.path);
  };

  const handleBreadcrumbClick = (breadcrumb) => {
    loadSubFolders(breadcrumb.path);
  };

  const navigateUpInDialog = () => {
    if (!folderDialog.breadcrumbs || folderDialog.breadcrumbs.length <= 1) {
      const assetsPath = `scenes/${organizationName}/${sceneName}/assets/`;
      loadSubFolders(assetsPath);
    } else {
      const currentBreadcrumbs = folderDialog.breadcrumbs;
      const parentBreadcrumb = currentBreadcrumbs[currentBreadcrumbs.length - 2];
      if (parentBreadcrumb) {
        loadSubFolders(parentBreadcrumb.path);
      }
    }
  };

  const handleFolderSelection = (folder) => {
    setConfirmDialog({
      open: true,
      folder: folder,
      message: `Are you sure you want to change ${title} to "${folder.name}"?`
    });
  };

  const openFolderDialog = () => {
    setFolderDialog({ open: true, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] });
    loadFolders();
  };

  const confirmFolderChange = () => {
    if (confirmDialog.folder && onFolderSelect && typeof onFolderSelect === 'function') {
      onFolderSelect(confirmDialog.folder);
    }
    setSelectedFolder(confirmDialog.folder);
    setConfirmDialog({ open: false, folder: null, message: '' });
    setFolderDialog({ open: false, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] });
  };

  const getFolderIcon = (type) => {
    switch (type) {
      case 'folder':
        return <Folder sx={{ fontSize: '2rem', color: '#fff' }} />;
      default:
        return <FolderOpen sx={{ fontSize: '2rem', color: '#fff' }} />;
    }
  };

  const getFolderTypeColor = (type) => {
    switch (type) {
      case 'folder':
        return 'rgba(255, 193, 7, 0.2)';
      default:
        return 'rgba(96, 125, 139, 0.2)';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return null;
  }

  return (
    <>
    <ListItem
      sx={{
        m: 0,
        p: 0,
        color: selectedFolder ? 'seafoamGreen' : '#fff'
      }}
      secondaryAction={
        <Box
          sx={{
            position: 'absolute',
            right: -12,
            top: 0,
            transform: 'translateY(-50%)',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={openFolderDialog}
            startIcon={<Folder />}
            sx={{
              fontWeight: 'bold',
              p: 0.2,
              pl: 0.8,
              pr: 0.8,
              borderColor: '#3a3a3a',
              color: '#fff',
              fontSize: '0.75rem',
              minWidth: 'auto',
              '&:hover': {
                borderColor: '#fff',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Change
          </Button>
        </Box>
      }
    >
      <ListItemButton
        sx={{ 
          m: 0, 
          p: 0.1, 
          pl: 1, 
          pt: 0.5, 
          pb: 0.5, 
          borderRadius: 1, 
          borderLeft: selectedFolder ? '3px solid seafoamGreen' : '3px solid rgba(69, 255, 167, 0)'
        }}
        onClick={openFolderDialog}
      >
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ color: selectedFolder ? 'seafoamGreen' : '#fff', fontWeight: 500 }}>
                {title}
              </Typography>
              {selectedFolder && (
                <Typography variant="caption" sx={{ color: '#45FFA7' , fontSize: '0.875rem' }}>
                  selected Folder: {selectedFolder.path}
                </Typography>
              )}
              {selectedPath && !selectedFolder && (
                <Typography variant="caption" sx={{ color: 'seafoamGreen', fontSize: '0.75rem' }}>
                  {selectedPath.split('/').pop() || selectedPath}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>

    <Dialog
        open={folderDialog.open}
        onClose={() => setFolderDialog({ open: false, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] })}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            minHeight: '30vh',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Folder Navigation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {folderDialog.breadcrumbs && folderDialog.breadcrumbs.length > 0 && folderDialog.breadcrumbs.map((breadcrumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Typography sx={{ color: '#ccc', fontSize: '0.875rem' }}>/</Typography>
                  )}
                  <Button
                    size="small"
                    onClick={() => handleBreadcrumbClick(breadcrumb)}
                    sx={{
                      color: index === folderDialog.breadcrumbs.length - 1 ? '#45FFA7' : '#fff',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      minWidth: 'auto',
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {breadcrumb.name}
                  </Button>
                </React.Fragment>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Go up one level">
              <IconButton 
                onClick={navigateUpInDialog}
                disabled={!folderDialog.breadcrumbs || folderDialog.breadcrumbs.length <= 1}
                sx={{ color: '#fff' }}
              >
                <FolderOpen />
              </IconButton>
            </Tooltip>
            <IconButton 
              onClick={() => setFolderDialog({ open: false, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] })}
              sx={{ color: '#fff' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flexGrow: 1 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
              {placeholder}
            </Typography>
          </Box>

          <Box sx={{ height: '400px', overflow: 'auto' }}>
            {folderDialog.folders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  No subfolders available
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {folderDialog.folders && folderDialog.folders.map((folder, index) => (
                  <Fragment key={index}>
                    <ListItem
                      button
                      onClick={() => setFolderDialog(prev => ({ ...prev, selectedFolder: folder }))}
                      onDoubleClick={() => handleDialogFolderDoubleClick(folder)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)'
                        },
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        bgcolor: folderDialog.selectedFolder?.path === folder.path ? 
                          'rgb(181, 252, 219, 10% )' : 'transparent'
                      }}
                    >
                      <ListItemIcon>
                        {getFolderIcon(folder.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                            {folder.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            <Chip 
                              label={folder.type} 
                              size="small" 
                              sx={{ 
                                bgcolor: getFolderTypeColor(folder.type),
                                color: '#fff',
                                fontSize: '0.75rem'
                              }} 
                            />
                            <Typography variant="caption" sx={{ color: '#ccc' }}>
                              {folder.path}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < folderDialog.folders.length - 1 && <Divider />}
                  </Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={() => setFolderDialog({ open: false, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] })}
            sx={{ color: '#ccc' }}
          >
            Cancel
          </Button>
          {folderDialog.selectedFolder && (
            <Button 
              variant="contained"
              onClick={() => {
                handleFolderSelection(folderDialog.selectedFolder);
                setFolderDialog({ open: false, folders: [], selectedFolder: null, currentPath: '', breadcrumbs: [] });
              }}
              sx={{
                bgcolor: '#45FFA785',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#45FFA785' }
              }}
            >
              Select Folder
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, folder: null, message: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Folder Change
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            {confirmDialog.message}
          </Typography>
          {confirmDialog.folder && (
            <Box sx={{ 
              p: 2, 
              // bgcolor: 'rgba(255, 193, 7, 0.1)', 
              borderRadius: 1, 
              // border: '1px solid #FFC107',
              mb: 2
            }}>
              <Typography variant="body2" sx={{ color: '#45FFA7', fontWeight: 500 }}>
                {confirmDialog.folder.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#ccc' }}>
                {confirmDialog.folder.path}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #333' }}>
          <Button 
            onClick={() => setConfirmDialog({ open: false, folder: null, message: '' })}
            sx={{ color: '#ccc' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmFolderChange}
            variant="contained"
            sx={{ 
              // bgcolor: '#FFC107',
              // color: '#000',
              '&:hover': {
                // bgcolor: '#FFB300'
              }
            }}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderSelector;
